import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { buildJobFlowSankey } from '../utils/data/build-job-flow-sankey';
import { hasAnyPositiveLink, scaleSankeyData } from '../utils/data/sankey';
import { isBrowsedJobKey } from '../types/user-state';
import type { SankeyData } from '../types/visualization';

function extractBrowsedJobsFromEntries(
  entries: RootState['userState']['entries'] | undefined
): Array<{ id: string }> {
  if (!entries) return [];
  const browsed: Array<{ id: string }> = [];
  Object.entries(entries).forEach(([itemKey, entry]) => {
    if (!isBrowsedJobKey(itemKey) || !entry?.data) return;
    const entryData = entry.data as Record<string, unknown>;
    const jobId = entryData.id ?? entryData.objectID;
    if (jobId) {
      browsed.push({ id: String(jobId) });
    }
  });
  return browsed;
}

export function useJobFlowSankeyFromRedux(): { scaledData: SankeyData | null; hasData: boolean } {
  const intl = useIntl();
  const viewedJobs = useSelector((state: RootState) => state.jobs.viewedJobs);
  const savedJobs = useSelector((state: RootState) => state.jobs.savedJobs);
  const appliedJobs = useSelector((state: RootState) => state.jobs.appliedJobs);
  const userState = useSelector((state: RootState) => state.userState.entries);

  return useMemo(() => {
    const labels = {
      browsed: intl.formatMessage({
        id: 'tools.jobFlowSankey.browsed',
        defaultMessage: 'Browsed',
      }),
      viewed: intl.formatMessage({
        id: 'tools.jobFlowSankey.viewed',
        defaultMessage: 'Viewed',
      }),
      saved: intl.formatMessage({
        id: 'tools.jobFlowSankey.saved',
        defaultMessage: 'Saved',
      }),
      applied: intl.formatMessage({
        id: 'tools.jobFlowSankey.applied',
        defaultMessage: 'Applied',
      }),
      interviewing: intl.formatMessage({
        id: 'tools.jobFlowSankey.interviewing',
        defaultMessage: 'Interviewing',
      }),
      offer: intl.formatMessage({
        id: 'tools.jobFlowSankey.offer',
        defaultMessage: 'Offer',
      }),
      rejected: intl.formatMessage({
        id: 'tools.jobFlowSankey.rejected',
        defaultMessage: 'Rejected',
      }),
      hired: intl.formatMessage({
        id: 'tools.jobFlowSankey.hired',
        defaultMessage: 'Hired',
      }),
    };

    const browsedJobs = extractBrowsedJobsFromEntries(userState);
    const raw = buildJobFlowSankey(
      browsedJobs,
      viewedJobs.map((j) => ({ id: j.id, applicationState: j.applicationState })),
      savedJobs.map((j) => ({ id: j.id, applicationState: j.applicationState })),
      appliedJobs.map((j) => ({ id: j.id, applicationState: j.applicationState })),
      labels
    );

    if (!hasAnyPositiveLink(raw)) {
      return { scaledData: null, hasData: false };
    }

    return { scaledData: scaleSankeyData(raw), hasData: true };
  }, [intl, viewedJobs, savedJobs, appliedJobs, userState]);
}
